import { useEffect, useState } from 'react'
import { supabase } from '../supabaseClient'
import { useAuth } from '../context/AuthContext'
import { useParams, useNavigate } from 'react-router-dom'
import { logAction } from '../lib/audit'
import Navbar from '../components/Navbar'
import ApplyProgramModal from '../components/ApplyProgramModal'
import EditAssignmentModal from '../components/EditAssignmentModal'
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function SortableAssignment({ assignment, onRemove, onEdit }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: assignment.id })
  function scheduleLabel() {
    if (assignment.frequency === 'daily') return 'Every day'
    if (assignment.days_of_week?.length > 0) return assignment.days_of_week.map(d => DAY_LABELS[d]).join(', ')
    return ''
  }
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }} className="card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span {...attributes} {...listeners} style={{ cursor: 'grab', color: 'var(--text-tertiary)', fontSize: 20, lineHeight: 1, flexShrink: 0 }}>⠿</span>
        {assignment.exercises.media_url && (
          <img src={assignment.exercises.media_url} alt={assignment.exercises.title} style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>{assignment.exercises.title}</p>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
            {(assignment.sets || assignment.exercises.sets) && <span className="badge">{assignment.sets || assignment.exercises.sets} sets</span>}
            {(assignment.reps || assignment.exercises.reps) && <span className="badge">{assignment.reps || assignment.exercises.reps} reps</span>}
            {(assignment.duration_seconds || assignment.exercises.duration_seconds) && <span className="badge">{assignment.duration_seconds || assignment.exercises.duration_seconds}s</span>}
            {scheduleLabel() && <span className="badge" style={{ background: '#f0fdf4', color: '#15803d' }}>{scheduleLabel()}</span>}
            {assignment.num_weeks && <span className="badge" style={{ background: '#f0fdf4', color: '#15803d' }}>{assignment.num_weeks} weeks</span>}
          </div>
          {assignment.clinician_note && <p style={{ fontSize: 12, fontStyle: 'italic', marginTop: 2 }}>📝 {assignment.clinician_note}</p>}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
          <button className="secondary" onClick={() => onEdit(assignment)} style={{ fontSize: 12, padding: '6px 10px' }}>Edit</button>
          <button className="danger" onClick={() => onRemove(assignment.id)}>Remove</button>
        </div>
      </div>
    </div>
  )
}

function TaskAssignmentCard({ assignment, onRemove, onEdit }) {
  function scheduleLabel() {
    if (assignment.frequency === 'daily') return 'Every day'
    if (assignment.days_of_week?.length > 0) return assignment.days_of_week.map(d => DAY_LABELS[d]).join(', ')
    return ''
  }
  return (
    <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      {assignment.interventions.media_url && (
        <img src={assignment.interventions.media_url} alt={assignment.interventions.title} style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>{assignment.interventions.title}</p>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
          {scheduleLabel() && <span className="badge" style={{ background: '#f0fdf4', color: '#15803d' }}>{scheduleLabel()}</span>}
          {assignment.num_weeks && <span className="badge" style={{ background: '#f0fdf4', color: '#15803d' }}>{assignment.num_weeks} weeks</span>}
        </div>
        {assignment.clinician_note && <p style={{ fontSize: 12, fontStyle: 'italic', marginTop: 2 }}>📝 {assignment.clinician_note}</p>}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
        <button className="secondary" onClick={() => onEdit(assignment)} style={{ fontSize: 12, padding: '6px 10px' }}>Edit</button>
        <button className="danger" onClick={() => onRemove(assignment.id)}>Remove</button>
      </div>
    </div>
  )
}

export default function PatientProgram() {
  const { patientId } = useParams()
  const { profile } = useAuth()
  const navigate = useNavigate()
  const [patient, setPatient] = useState(null)
  const [assignments, setAssignments] = useState([])
  const [interventionAssignments, setInterventionAssignments] = useState([])
  const [exercises, setExercises] = useState([])
  const [interventions, setInterventions] = useState([])
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState('exercises')
  const [showDiagnosisModal, setShowDiagnosisModal] = useState(false)
  const [editingAssignment, setEditingAssignment] = useState(null)
  const [editingType, setEditingType] = useState(null)

  // Add exercise states
  const [exerciseMode, setExerciseMode] = useState('library')
  const [exerciseSearch, setExerciseSearch] = useState('')
  const [showExerciseDropdown, setShowExerciseDropdown] = useState(false)
  const [selectedExercise, setSelectedExercise] = useState('')
  const [newExTitle, setNewExTitle] = useState('')
  const [newExDescription, setNewExDescription] = useState('')
  const [newExInstructions, setNewExInstructions] = useState('')
  const [newExFile, setNewExFile] = useState(null)
  const [newExVideoUrl, setNewExVideoUrl] = useState('')
  const [saveToLibrary, setSaveToLibrary] = useState(true)
  const [sets, setSets] = useState('')
  const [reps, setReps] = useState('')
  const [duration, setDuration] = useState('')
  const [frequency, setFrequency] = useState('daily')
  const [selectedDays, setSelectedDays] = useState([])
  const [numWeeks, setNumWeeks] = useState('')
  const [note, setNote] = useState('')

  // Add task states
  const [selectedIntervention, setSelectedIntervention] = useState('')
  const [taskMode, setTaskMode] = useState('library')
  const [taskSearch, setTaskSearch] = useState('')
  const [showTaskDropdown, setShowTaskDropdown] = useState(false)
  const [newTaskTitle, setNewTaskTitle] = useState('')
  const [newTaskDescription, setNewTaskDescription] = useState('')
  const [newTaskInstructions, setNewTaskInstructions] = useState('')
  const [newTaskFile, setNewTaskFile] = useState(null)
  const [newTaskVideoUrl, setNewTaskVideoUrl] = useState('')
  const [saveTaskToLibrary, setSaveTaskToLibrary] = useState(true)

  const sensors = useSensors(useSensor(PointerSensor))

  useEffect(() => { fetchAll(); logAction(profile?.id, 'view_patient', { patient_id: patientId }) }, [])

  async function fetchAll() {
    const [{ data: patientData }, { data: assignmentData }, { data: interventionAssignmentData }, { data: exerciseData }, { data: interventionData }] = await Promise.all([
      supabase.from('users').select('*').eq('id', patientId).single(),
      supabase.from('assignments').select('*, exercises(*)').eq('patient_id', patientId).order('sort_order'),
      supabase.from('intervention_assignments').select('*, interventions(*)').eq('patient_id', patientId).order('sort_order'),
      supabase.from('exercises').select('*').eq('clinic_id', profile.clinic_id).eq('is_library', true).order('title'),
      supabase.from('interventions').select('*').eq('clinic_id', profile.clinic_id).order('title')
    ])
    setPatient(patientData)
    setAssignments(assignmentData || [])
    setInterventionAssignments(interventionAssignmentData || [])
    setExercises(exerciseData || [])
    setInterventions(interventionData || [])
    setLoading(false)
  }

  function toggleDay(day) {
    setSelectedDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day].sort())
  }

  async function handleAddExercise() {
    let exerciseId = selectedExercise

    if (exerciseMode === 'new') {
      if (!newExTitle.trim()) return
      let newMediaUrl = null
      if (newExFile) {
        const ext = newExFile.name.split('.').pop()
        const filename = Date.now() + '.' + ext
        const { error: uploadError } = await supabase.storage.from('exercises').upload(filename, newExFile)
        if (!uploadError) {
          const { data: urlData } = supabase.storage.from('exercises').getPublicUrl(filename)
          newMediaUrl = urlData.publicUrl
        }
      }
      const { data: newEx } = await supabase.from('exercises').insert({
        clinic_id: profile.clinic_id,
        title: newExTitle,
        description: newExDescription,
        instructions: newExInstructions,
        is_library: saveToLibrary,
        media_url: newMediaUrl,
        video_url: newExVideoUrl || null
      }).select().single()
      if (!newEx) return
      exerciseId = newEx.id
      if (saveToLibrary) setExercises(prev => [...prev, newEx])
    }

    if (!exerciseId) return
    const { data } = await supabase.from('assignments').insert({
      patient_id: patientId, exercise_id: exerciseId, clinician_note: note,
      sort_order: assignments.length,
      sets: sets ? parseInt(sets) : null,
      reps: reps ? parseInt(reps) : null,
      duration_seconds: duration ? parseInt(duration) : null,
      frequency, days_of_week: frequency === 'custom' ? selectedDays : null,
      num_weeks: numWeeks ? parseInt(numWeeks) : null
    }).select('*, exercises(*)').single()
    setAssignments(prev => [...prev, data])
    setSelectedExercise(''); setExerciseSearch(''); setNote(''); setSets(''); setReps(''); setDuration('')
    setFrequency('daily'); setSelectedDays([]); setNumWeeks('')
    setNewExTitle(''); setNewExDescription(''); setNewExInstructions(''); setSaveToLibrary(true)
    setNewExFile(null); setNewExVideoUrl('')
  }

  async function handleAddIntervention() {
    let interventionId = selectedIntervention

    if (taskMode === 'new') {
      if (!newTaskTitle.trim()) return
      let newMediaUrl = null
      if (newTaskFile) {
        const ext = newTaskFile.name.split('.').pop()
        const filename = Date.now() + '.' + ext
        const { error: uploadError } = await supabase.storage.from('exercises').upload(filename, newTaskFile)
        if (!uploadError) {
          const { data: urlData } = supabase.storage.from('exercises').getPublicUrl(filename)
          newMediaUrl = urlData.publicUrl
        }
      }
      const { data: newTask } = await supabase.from('interventions').insert({
        clinic_id: profile.clinic_id,
        title: newTaskTitle,
        description: newTaskDescription,
        instructions: newTaskInstructions,
        media_url: newMediaUrl,
        video_url: newTaskVideoUrl || null
      }).select().single()
      if (!newTask) return
      interventionId = newTask.id
      if (saveTaskToLibrary) setInterventions(prev => [...prev, newTask])
    }

    if (!interventionId) return
    const { data, error } = await supabase.from('intervention_assignments').insert({
      patient_id: patientId, intervention_id: interventionId, clinician_note: note,
      sort_order: interventionAssignments.length,
      frequency, days_of_week: frequency === 'custom' ? selectedDays : null,
      num_weeks: numWeeks ? parseInt(numWeeks) : null
    }).select('*, interventions(*)').single()
    if (error) { console.error(error); return }
    if (data) setInterventionAssignments(prev => [...prev, data])
    setSelectedIntervention(''); setTaskSearch(''); setNote(''); setFrequency('daily'); setSelectedDays([]); setNumWeeks('')
    setNewTaskTitle(''); setNewTaskDescription(''); setNewTaskInstructions('')
    setNewTaskFile(null); setNewTaskVideoUrl(''); setSaveTaskToLibrary(true)
    fetchAll()
  }

  async function handleRemoveExercise(id) {
    if (!confirm('Remove this exercise?')) return
    await supabase.from('assignments').delete().eq('id', id)
    setAssignments(prev => prev.filter(a => a.id !== id))
  }

  async function handleRemoveIntervention(id) {
    if (!confirm('Remove this task?')) return
    await supabase.from('intervention_assignments').delete().eq('id', id)
    setInterventionAssignments(prev => prev.filter(a => a.id !== id))
  }

  async function handleDragEnd(event) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = assignments.findIndex(a => a.id === active.id)
    const newIndex = assignments.findIndex(a => a.id === over.id)
    const reordered = arrayMove(assignments, oldIndex, newIndex)
    setAssignments(reordered)
    await Promise.all(reordered.map((a, i) => supabase.from('assignments').update({ sort_order: i }).eq('id', a.id)))
  }

  if (loading) return <><Navbar /><div className="page"><p>Loading...</p></div></>

  return (
    <>
      <Navbar />
      <div className="page">
        <span onClick={() => navigate('/clinician/patients')} className="back-link" style={{ cursor: 'pointer' }}>← All patients</span>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
          <div>
            <h1 style={{ marginBottom: 4 }}>{patient?.full_name}</h1>
            <p>{assignments.length} exercise{assignments.length !== 1 ? 's' : ''} · {interventionAssignments.length} task{interventionAssignments.length !== 1 ? 's' : ''}</p>
          </div>
          <button className="secondary" onClick={() => navigate('/clinician/patients/' + patientId + '/compliance')} style={{ fontSize: 13, padding: '8px 14px' }}>
            View compliance →
          </button>
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: '1.5rem' }}>
          <button type="button" onClick={() => setTab('exercises')} style={{
            flex: 1, padding: '10px', fontSize: 14,
            background: tab === 'exercises' ? 'var(--brand)' : 'var(--surface)',
            color: tab === 'exercises' ? 'white' : 'var(--text-primary)',
            border: tab === 'exercises' ? 'none' : '1.5px solid var(--border)'
          }}>🏋️ Exercises</button>
          <button type="button" onClick={() => setTab('tasks')} style={{
            flex: 1, padding: '10px', fontSize: 14,
            background: tab === 'tasks' ? 'var(--brand)' : 'var(--surface)',
            color: tab === 'tasks' ? 'white' : 'var(--text-primary)',
            border: tab === 'tasks' ? 'none' : '1.5px solid var(--border)'
          }}>✅ Tasks</button>
        </div>

        {tab === 'exercises' && (
          <>
            <button type="button" onClick={() => setShowDiagnosisModal(true)} style={{
              width: '100%', marginBottom: '1rem', padding: '12px',
              background: '#fdf4ff', color: '#7e22ce',
              border: '1.5px solid #e9d5ff', borderRadius: 10,
              fontSize: 14, fontWeight: 600,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
            }}>
              📋 Apply program
            </button>

            <div className="card" style={{ marginBottom: '1.5rem' }}>
              <h2 style={{ marginBottom: '1rem', fontSize: 15 }}>Add exercise</h2>
              <div className="form-group">
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                  <button type="button" onClick={() => setExerciseMode('library')} style={{ flex: 1, padding: '8px', fontSize: 13, borderRadius: 8, background: exerciseMode === 'library' ? 'var(--brand)' : 'var(--surface)', color: exerciseMode === 'library' ? 'white' : 'var(--text-primary)', border: exerciseMode === 'library' ? 'none' : '1.5px solid var(--border)' }}>From library</button>
                  <button type="button" onClick={() => setExerciseMode('new')} style={{ flex: 1, padding: '8px', fontSize: 13, borderRadius: 8, background: exerciseMode === 'new' ? 'var(--brand)' : 'var(--surface)', color: exerciseMode === 'new' ? 'white' : 'var(--text-primary)', border: exerciseMode === 'new' ? 'none' : '1.5px solid var(--border)' }}>Create new</button>
                </div>

                {exerciseMode === 'library' && (
                  <div style={{ position: 'relative' }}>
                    <input
                      placeholder="Search your exercise library..."
                      value={exerciseSearch}
                      onChange={e => { setExerciseSearch(e.target.value); setShowExerciseDropdown(true); setSelectedExercise('') }}
                      onFocus={() => setShowExerciseDropdown(true)}
                      onBlur={() => setTimeout(() => setShowExerciseDropdown(false), 150)}
                      style={{ width: '100%' }}
                    />
                    {showExerciseDropdown && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 10, zIndex: 100, maxHeight: 200, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                        {exercises.filter(ex => ex.title.toLowerCase().includes(exerciseSearch.toLowerCase())).map(ex => (
                          <div key={ex.id} onMouseDown={() => { setSelectedExercise(ex.id); setExerciseSearch(ex.title); setShowExerciseDropdown(false) }}
                            style={{ padding: '10px 14px', cursor: 'pointer', fontSize: 14, borderBottom: '1px solid var(--border)' }}
                            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                            {ex.title}
                          </div>
                        ))}
                        {exercises.filter(ex => ex.title.toLowerCase().includes(exerciseSearch.toLowerCase())).length === 0 && (
                          <div style={{ padding: '10px 14px', fontSize: 14, color: 'var(--text-tertiary)' }}>No exercises found</div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {exerciseMode === 'new' && (
                  <>
                    <div className="form-field">
                      <label className="form-label">Exercise title</label>
                      <input placeholder="e.g. Clamshell" value={newExTitle} onChange={e => setNewExTitle(e.target.value)} />
                    </div>
                    <div className="form-field">
                      <label className="form-label">Description (optional)</label>
                      <input placeholder="Brief summary" value={newExDescription} onChange={e => setNewExDescription(e.target.value)} />
                    </div>
                    <div className="form-field">
                      <label className="form-label">Instructions (optional)</label>
                      <textarea placeholder="Step by step instructions..." value={newExInstructions} onChange={e => setNewExInstructions(e.target.value)} rows={3} style={{ resize: 'vertical' }} />
                    </div>
                    <div className="form-field">
                      <label className="form-label">Image (optional)</label>
                      <input type="file" accept="image/png,image/jpeg" onChange={e => setNewExFile(e.target.files[0])} />
                      {newExFile && <p style={{ fontSize: 12, color: 'var(--success)', marginTop: 4 }}>✓ {newExFile.name}</p>}
                    </div>
                    <div className="form-field">
                      <label className="form-label">YouTube URL (optional)</label>
                      <input type="url" placeholder="https://youtube.com/watch?v=..." value={newExVideoUrl} onChange={e => setNewExVideoUrl(e.target.value)} />
                    </div>
                  </>
                )}

                {(selectedExercise || exerciseMode === 'new') && (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                      <div className="form-field">
                        <label className="form-label" style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>Sets</label>
                        <input placeholder="3" type="number" min="1" value={sets} onChange={e => setSets(e.target.value)} />
                      </div>
                      <div className="form-field">
                        <label className="form-label" style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>Reps</label>
                        <input placeholder="10" type="number" min="1" value={reps} onChange={e => setReps(e.target.value)} />
                      </div>
                      <div className="form-field">
                        <label className="form-label" style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>Duration (sec)</label>
                        <input placeholder="30" type="number" min="1" value={duration} onChange={e => setDuration(e.target.value)} />
                      </div>
                    </div>
                    <div className="form-field">
                      <label className="form-label">Schedule</label>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button type="button" onClick={() => setFrequency('daily')} style={{ padding: '8px 16px', fontSize: 13, borderRadius: 8, background: frequency === 'daily' ? 'var(--brand)' : 'var(--surface)', color: frequency === 'daily' ? 'white' : 'var(--text-primary)', border: frequency === 'daily' ? 'none' : '1.5px solid var(--border)' }}>Daily</button>
                        <button type="button" onClick={() => setFrequency('custom')} style={{ padding: '8px 16px', fontSize: 13, borderRadius: 8, background: frequency === 'custom' ? 'var(--brand)' : 'var(--surface)', color: frequency === 'custom' ? 'white' : 'var(--text-primary)', border: frequency === 'custom' ? 'none' : '1.5px solid var(--border)' }}>Specific days</button>
                      </div>
                    </div>
                    {frequency === 'custom' && (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {DAY_LABELS.map((label, i) => (
                          <button key={i} type="button" onClick={() => toggleDay(i)} style={{ width: 42, height: 42, borderRadius: '50%', fontSize: 12, fontWeight: 600, padding: 0, background: selectedDays.includes(i) ? 'var(--brand)' : 'var(--surface)', color: selectedDays.includes(i) ? 'white' : 'var(--text-secondary)', border: selectedDays.includes(i) ? 'none' : '1.5px solid var(--border)' }}>{label}</button>
                        ))}
                      </div>
                    )}
                    <div className="form-field">
                      <label className="form-label">Number of weeks</label>
                      <input placeholder="e.g. 4" type="number" min="1" max="52" value={numWeeks} onChange={e => setNumWeeks(e.target.value)} />
                    </div>
                    <input placeholder="Optional note for patient" value={note} onChange={e => setNote(e.target.value)} />
                  </>
                )}

                {exerciseMode === 'new' && (
                  <div onClick={() => setSaveToLibrary(prev => !prev)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: saveToLibrary ? 'var(--brand-light)' : 'var(--bg)', border: '1.5px solid ' + (saveToLibrary ? 'var(--brand)' : 'var(--border)'), borderRadius: 8, cursor: 'pointer' }}>
                    <div style={{ width: 18, height: 18, borderRadius: 4, background: saveToLibrary ? 'var(--brand)' : 'white', border: '1.5px solid ' + (saveToLibrary ? 'var(--brand)' : 'var(--border-strong)'), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {saveToLibrary && <span style={{ color: 'white', fontSize: 12, fontWeight: 700 }}>✓</span>}
                    </div>
                    <p style={{ margin: 0, fontSize: 13, color: saveToLibrary ? 'var(--brand)' : 'var(--text-secondary)', fontWeight: saveToLibrary ? 600 : 400 }}>Save to exercise library for future use</p>
                  </div>
                )}

                <button onClick={handleAddExercise} disabled={exerciseMode === 'library' ? !selectedExercise : !newExTitle.trim()}>Add to program</button>
              </div>
            </div>

            {assignments.length === 0 && (
              <div className="empty-state">
                <div className="empty-state-icon">📋</div>
                <h2 style={{ marginBottom: 8 }}>No exercises yet</h2>
                <p>Apply a program template or add exercises manually</p>
              </div>
            )}

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={assignments.map(a => a.id)} strategy={verticalListSortingStrategy}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {assignments.map(assignment => (
                    <SortableAssignment
                      key={assignment.id}
                      assignment={assignment}
                      onRemove={handleRemoveExercise}
                      onEdit={a => { setEditingAssignment(a); setEditingType('exercise') }}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </>
        )}

        {tab === 'tasks' && (
          <>
            <div className="card" style={{ marginBottom: '1.5rem' }}>
              <h2 style={{ marginBottom: '1rem', fontSize: 15 }}>Add task</h2>
              <div className="form-group">
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                  <button type="button" onClick={() => setTaskMode('library')} style={{ flex: 1, padding: '8px', fontSize: 13, borderRadius: 8, background: taskMode === 'library' ? 'var(--brand)' : 'var(--surface)', color: taskMode === 'library' ? 'white' : 'var(--text-primary)', border: taskMode === 'library' ? 'none' : '1.5px solid var(--border)' }}>From library</button>
                  <button type="button" onClick={() => setTaskMode('new')} style={{ flex: 1, padding: '8px', fontSize: 13, borderRadius: 8, background: taskMode === 'new' ? 'var(--brand)' : 'var(--surface)', color: taskMode === 'new' ? 'white' : 'var(--text-primary)', border: taskMode === 'new' ? 'none' : '1.5px solid var(--border)' }}>Create new</button>
                </div>

                {taskMode === 'library' && (
                  <div style={{ position: 'relative' }}>
                    <input
                      placeholder="Search your task library..."
                      value={taskSearch}
                      onChange={e => { setTaskSearch(e.target.value); setShowTaskDropdown(true); setSelectedIntervention('') }}
                      onFocus={() => setShowTaskDropdown(true)}
                      onBlur={() => setTimeout(() => setShowTaskDropdown(false), 150)}
                      style={{ width: '100%' }}
                    />
                    {showTaskDropdown && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 10, zIndex: 100, maxHeight: 200, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                        {interventions.filter(i => i.title.toLowerCase().includes(taskSearch.toLowerCase())).map(i => (
                          <div key={i.id} onMouseDown={() => { setSelectedIntervention(i.id); setTaskSearch(i.title); setShowTaskDropdown(false) }}
                            style={{ padding: '10px 14px', cursor: 'pointer', fontSize: 14, borderBottom: '1px solid var(--border)' }}
                            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                            {i.title}
                          </div>
                        ))}
                        {interventions.filter(i => i.title.toLowerCase().includes(taskSearch.toLowerCase())).length === 0 && (
                          <div style={{ padding: '10px 14px', fontSize: 14, color: 'var(--text-tertiary)' }}>No tasks found</div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {taskMode === 'new' && (
                  <>
                    <div className="form-field">
                      <label className="form-label">Task title</label>
                      <input placeholder="e.g. Ice Therapy" value={newTaskTitle} onChange={e => setNewTaskTitle(e.target.value)} />
                    </div>
                    <div className="form-field">
                      <label className="form-label">Description (optional)</label>
                      <input placeholder="Brief summary" value={newTaskDescription} onChange={e => setNewTaskDescription(e.target.value)} />
                    </div>
                    <div className="form-field">
                      <label className="form-label">Instructions (optional)</label>
                      <textarea placeholder="Step by step instructions..." value={newTaskInstructions} onChange={e => setNewTaskInstructions(e.target.value)} rows={3} style={{ resize: 'vertical' }} />
                    </div>
                    <div className="form-field">
                      <label className="form-label">Image (optional)</label>
                      <input type="file" accept="image/png,image/jpeg" onChange={e => setNewTaskFile(e.target.files[0])} />
                      {newTaskFile && <p style={{ fontSize: 12, color: 'var(--success)', marginTop: 4 }}>✓ {newTaskFile.name}</p>}
                    </div>
                  </>
                )}

                {(selectedIntervention || taskMode === 'new') && (
                  <>
                    <div className="form-field">
                      <label className="form-label">Schedule</label>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button type="button" onClick={() => setFrequency('daily')} style={{ padding: '8px 16px', fontSize: 13, borderRadius: 8, background: frequency === 'daily' ? 'var(--brand)' : 'var(--surface)', color: frequency === 'daily' ? 'white' : 'var(--text-primary)', border: frequency === 'daily' ? 'none' : '1.5px solid var(--border)' }}>Daily</button>
                        <button type="button" onClick={() => setFrequency('custom')} style={{ padding: '8px 16px', fontSize: 13, borderRadius: 8, background: frequency === 'custom' ? 'var(--brand)' : 'var(--surface)', color: frequency === 'custom' ? 'white' : 'var(--text-primary)', border: frequency === 'custom' ? 'none' : '1.5px solid var(--border)' }}>Specific days</button>
                      </div>
                    </div>
                    {frequency === 'custom' && (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {DAY_LABELS.map((label, i) => (
                          <button key={i} type="button" onClick={() => toggleDay(i)} style={{ width: 42, height: 42, borderRadius: '50%', fontSize: 12, fontWeight: 600, padding: 0, background: selectedDays.includes(i) ? 'var(--brand)' : 'var(--surface)', color: selectedDays.includes(i) ? 'white' : 'var(--text-secondary)', border: selectedDays.includes(i) ? 'none' : '1.5px solid var(--border)' }}>{label}</button>
                        ))}
                      </div>
                    )}
                    <div className="form-field">
                      <label className="form-label">Number of weeks</label>
                      <input placeholder="e.g. 4" type="number" min="1" max="52" value={numWeeks} onChange={e => setNumWeeks(e.target.value)} />
                    </div>
                    <input placeholder="Optional note for patient" value={note} onChange={e => setNote(e.target.value)} />
                  </>
                )}
                {taskMode === 'new' && (
                  <div onClick={() => setSaveTaskToLibrary(prev => !prev)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: saveTaskToLibrary ? 'var(--brand-light)' : 'var(--bg)', border: '1.5px solid ' + (saveTaskToLibrary ? 'var(--brand)' : 'var(--border)'), borderRadius: 8, cursor: 'pointer' }}>
                    <div style={{ width: 18, height: 18, borderRadius: 4, background: saveTaskToLibrary ? 'var(--brand)' : 'white', border: '1.5px solid ' + (saveTaskToLibrary ? 'var(--brand)' : 'var(--border-strong)'), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {saveTaskToLibrary && <span style={{ color: 'white', fontSize: 12, fontWeight: 700 }}>✓</span>}
                    </div>
                    <p style={{ margin: 0, fontSize: 13, color: saveTaskToLibrary ? 'var(--brand)' : 'var(--text-secondary)', fontWeight: saveTaskToLibrary ? 600 : 400 }}>Save to task library for future use</p>
                  </div>
                )}
                <button onClick={handleAddIntervention} disabled={taskMode === 'library' ? !selectedIntervention : !newTaskTitle.trim()}>Add to program</button>
              </div>
            </div>

            {interventionAssignments.length === 0 && (
              <div className="empty-state">
                <div className="empty-state-icon">✅</div>
                <h2 style={{ marginBottom: 8 }}>No tasks yet</h2>
                <p>Add tasks from your library above</p>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {interventionAssignments.map(assignment => (
                <TaskAssignmentCard
                  key={assignment.id}
                  assignment={assignment}
                  onRemove={handleRemoveIntervention}
                  onEdit={a => { setEditingAssignment(a); setEditingType('task') }}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {editingAssignment && (
        <EditAssignmentModal
          assignment={editingAssignment}
          type={editingType}
          onClose={() => { setEditingAssignment(null); setEditingType(null) }}
          onSaved={() => fetchAll()}
        />
      )}

      {showDiagnosisModal && (
        <ApplyProgramModal
          patientId={patientId}
          onClose={() => setShowDiagnosisModal(false)}
          onApplied={() => fetchAll()}
        />
      )}
    </>
  )
}